import type { BatchHealthcheckResult } from '../api/proxies';
import { duplicateIPv4GroupCount } from './healthcheckDuplicateIPs';

export type HealthcheckFilter = 'all' | 'abnormal' | 'duplicate';

type IPCounts = ReadonlyMap<string, number>;

export interface HealthcheckSummary {
  total: number;
  available: number;
  unavailable: number;
  skipped: number;
  abnormal: number;
  duplicateGroups: number;
}

const duplicateSize = (result: BatchHealthcheckResult, counts: IPCounts): number => {
  const ip = result.detected_ipv4?.trim();
  return result.ipv4_check_ok && ip ? counts.get(ip) || 0 : 0;
};

// Why a proxy needs attention; empty means it is healthy and unique.
export const abnormalReasons = (result: BatchHealthcheckResult, counts: IPCounts): string[] => {
  if (result.status === 'unavailable') return [`不可用：${result.error || '未知错误'}`];
  if (result.status === 'skipped') return [`已跳过：${result.error || '未检测'}`];
  const reasons: string[] = [];
  if (!result.ipv4_check_ok) reasons.push(`出口 IP 检测失败：${result.ipv4_error || '未知错误'}`);
  const shared = duplicateSize(result, counts);
  if (shared > 1) reasons.push(`出口 IP 与其它服务器重复（共 ${shared} 台）`);
  return reasons;
};

const rank = (result: BatchHealthcheckResult, counts: IPCounts): number => {
  if (result.status === 'unavailable') return 0;
  if (result.status === 'skipped') return 1;
  return abnormalReasons(result, counts).length > 0 ? 2 : 3;
};

// Problems first so they are never below the fold; ties keep id order.
export const sortResults = (
  results: readonly BatchHealthcheckResult[],
  counts: IPCounts,
): BatchHealthcheckResult[] =>
  [...results].sort((a, b) => rank(a, counts) - rank(b, counts) || a.proxy_id - b.proxy_id);

export const filterResults = (
  results: readonly BatchHealthcheckResult[],
  filter: HealthcheckFilter,
  counts: IPCounts,
): BatchHealthcheckResult[] => {
  if (filter === 'abnormal') return results.filter((r) => abnormalReasons(r, counts).length > 0);
  if (filter === 'duplicate') return results.filter((r) => duplicateSize(r, counts) > 1);
  return [...results];
};

export const summarize = (
  results: readonly BatchHealthcheckResult[],
  counts: IPCounts,
): HealthcheckSummary => ({
  total: results.length,
  available: results.filter((r) => r.status === 'available').length,
  unavailable: results.filter((r) => r.status === 'unavailable').length,
  skipped: results.filter((r) => r.status === 'skipped').length,
  abnormal: results.filter((r) => abnormalReasons(r, counts).length > 0).length,
  duplicateGroups: duplicateIPv4GroupCount(counts),
});

// Rechecked proxies replace their old row in place; unknown ids are appended.
export const mergeResults = (
  current: readonly BatchHealthcheckResult[],
  rechecked: readonly BatchHealthcheckResult[],
): BatchHealthcheckResult[] => {
  const byId = new Map(rechecked.map((r) => [r.proxy_id, r]));
  const merged = current.map((r) => byId.get(r.proxy_id) ?? r);
  const known = new Set(current.map((r) => r.proxy_id));
  return [...merged, ...rechecked.filter((r) => !known.has(r.proxy_id))];
};

// One line per proxy that needs attention, ready to paste into a chat.
export const abnormalReport = (results: readonly BatchHealthcheckResult[], counts: IPCounts): string =>
  sortResults(results, counts)
    .map((r) => ({ r, reasons: abnormalReasons(r, counts) }))
    .filter(({ reasons }) => reasons.length > 0)
    .map(({ r, reasons }) => `#${r.proxy_id} ${r.username} ${r.node_name}（${r.agent_ip}:${r.agent_port}）：${reasons.join('；')}`)
    .join('\n');
